import { ActionReducerMapBuilder, CaseReducer, PayloadAction } from "@reduxjs/toolkit";
import type { AdminState } from "../AdminDb";
import {
  BusinessSubscription,
  DEFAULT_BUSINESS_SUBSCRIPTION_STATUSES,
} from "../../../interfaces/AdminSubscriptionObject";
import {
  fetchAdminBusinessName,
  fetchAdminBusinessSubscriptions,
  fetchAdminSubscriptionCounts,
} from "../../../services/thunks/AdminBusinessesThunk";
import { AdminTabState, createAdminTabState, registerAsync } from "./adminTabState";

// Admin -> Businesses -> Subscriptions. Same file-owns-its-tab arrangement as
// approvals.ts.
//
// Note what this slice does NOT hold: the subscription PLAN catalog. That
// already lives in `state.subscriptions` (fetchSubscriptionPlans), and copying
// it here would create a second source of truth for the same rows. This tab
// reads plan names from there and keeps only what is admin-only.

export interface AdminSubscriptionsState extends AdminTabState {
  rows: BusinessSubscription[];
  /** Every subscription across all chip statuses (null = not loaded yet). Used
   *  ONLY to count each status chip; the list itself reads `rows`. */
  allRows: BusinessSubscription[] | null;
  /** Sent to the server comma-separated; filtering is server-side. */
  statusFilter: string[];
  search: string;
  /**
   * businessId -> display name, resolved one id at a time because the list
   * endpoint sends uuids only. A cache, so a name costs at most one request
   * per session - a lookup that FAILS is cached too, as `null` (see
   * attachSubscriptionsCases' onRejected), so a business whose name can't be
   * resolved isn't retried forever. Delete this the day the list carries
   * `businessName`.
   */
  businessNameById: Record<string, string | null>;
}

export const subscriptionsInitialState = (): AdminSubscriptionsState => ({
  ...createAdminTabState(),
  rows: [],
  allRows: null,
  statusFilter: [...DEFAULT_BUSINESS_SUBSCRIPTION_STATUSES],
  search: "",
  businessNameById: {},
});

export const SUBSCRIPTIONS_FETCH_KEY = "fetchBusinessSubscriptions";
export const SUBSCRIPTION_COUNTS_FETCH_KEY = "fetchBusinessSubscriptionCounts";

// ── Plain reducers ──────────────────────────────────────────────────────────

const setSubscriptionSearch: CaseReducer<AdminState, PayloadAction<string>> = (
  state,
  action
) => {
  state.subscriptions.search = action.payload;
};

/** Replaces the whole filter (the chips are a multi-select, and the endpoint
 *  takes the full list each time). Callers pass the new set, not a toggle. */
const setSubscriptionStatusFilter: CaseReducer<
  AdminState,
  PayloadAction<string[]>
> = (state, action) => {
  state.subscriptions.statusFilter = action.payload;
};

export const subscriptionsReducers = {
  setSubscriptionSearch,
  setSubscriptionStatusFilter,
};

// ── Async cases ─────────────────────────────────────────────────────────────

export function attachSubscriptionsCases(
  builder: ActionReducerMapBuilder<AdminState>
): void {
  registerAsync(builder, fetchAdminBusinessSubscriptions, {
    tab: (state) => state.subscriptions,
    key: SUBSCRIPTIONS_FETCH_KEY,
    loading: "Loading subscriptions...",
    onFulfilled: (state, payload) => {
      // Drop a response for a filter the user has already changed: chips can be
      // tapped faster than the network answers, and the newer request's result
      // must win regardless of which one lands last.
      if (payload.statuses.join(",") !== state.subscriptions.statusFilter.join(",")) {
        return;
      }
      state.subscriptions.rows = payload.rows;
    },
  });

  // Chip counts load separately and quietly - a failure just leaves the chips
  // without numbers (the tab only reads SUBSCRIPTIONS_FETCH_KEY's error).
  registerAsync(builder, fetchAdminSubscriptionCounts, {
    tab: (state) => state.subscriptions,
    key: SUBSCRIPTION_COUNTS_FETCH_KEY,
    onFulfilled: (state, payload) => {
      state.subscriptions.allRows = payload;
    },
    onRejected: () => true,
  });

  // Name resolution is deliberately quiet: it has its own per-id key, and a
  // failure is swallowed (the card falls back to the id) rather than shown.
  // The failure is still cached (as `null`), not just left unset - otherwise
  // SubscriptionsTab's resolve-effect (which only skips ids already present in
  // businessNameById) would re-dispatch the same failing fetch every time
  // `visible` changes reference.
  registerAsync(builder, fetchAdminBusinessName, {
    tab: (state) => state.subscriptions,
    key: (businessId) => `businessName_${businessId}`,
    onFulfilled: (state, payload) => {
      state.subscriptions.businessNameById[payload.businessId] = payload.name;
    },
    onRejected: (state, _message, businessId) => {
      state.subscriptions.businessNameById[businessId] = null;
      return true; // handled: never surface a name lookup failure
    },
  });
}

// ── Selectors (`any` state - see the note in approvals.ts) ──────────────────

export const selectAdminSubscriptions = (state: any): AdminSubscriptionsState =>
  state.admin.subscriptions;

export const selectAdminSubscriptionRows = (state: any): BusinessSubscription[] =>
  state.admin.subscriptions.rows;

/** Rows with the tab's search applied. Matches the resolved business name when
 *  one has arrived, and always the raw ids, so a search works before names do. */
export const selectVisibleAdminSubscriptions = (
  state: any
): BusinessSubscription[] => {
  const tab = state.admin.subscriptions as AdminSubscriptionsState;
  const term = tab.search.trim().toLowerCase();
  if (!term) return tab.rows;
  return tab.rows.filter((row) => {
    const name = tab.businessNameById[row.businessId] ?? "";
    return (
      name.toLowerCase().includes(term) ||
      row.businessId.toLowerCase().includes(term) ||
      row.subscriptionId.toLowerCase().includes(term)
    );
  });
};

export const selectAdminBusinessName = (
  state: any,
  businessId: string
): string | null | undefined => state.admin.subscriptions.businessNameById[businessId];
